import { lazy, memo, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { AdminLayout } from '@/components/layout/AdminLayout';
import { PortalLayout } from '@/features/report-viewer/layout/PortalLayout';
import { PATHS } from '@/constants/path';

import { ProtectedRoute } from './ProtectedRoute';

// Lazy-loaded pages using standard React.lazy
const LoginPage = lazy(() =>
  import('@/features/auth/pages/LoginPage').then(m => ({
    default: m.LoginPage,
  })),
);
const MsalRedirectHandler = lazy(() =>
  import('@/features/auth/components/MsalRedirectHandler').then(m => ({
    default: m.MsalRedirectHandler,
  })),
);
const ChangePasswordPage = lazy(() =>
  import('@/features/auth/pages/ChangePasswordPage').then(m => ({
    default: m.ChangePasswordPage,
  })),
);
const AdminRoutes = lazy(() =>
  import('@/features/admin/routes').then(m => ({ default: m.AdminRoutes })),
);
const LandingPage = lazy(() =>
  import('@/features/landing/pages/LandingPage').then(m => ({
    default: m.LandingPage,
  })),
);
const EmbeddedReportViewer = lazy(() =>
  import('@/features/report-viewer/pages/EmbeddedReportViewer').then(m => ({
    default: m.EmbeddedReportViewer,
  })),
);

const PageLoader = memo(function PageLoader() {
  return (
    <div className="flex-center h-screen w-screen">
      <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
    </div>
  );
});

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route
          path={PATHS.LOGIN}
          element={<LoginPage />}
        />
        <Route
          path={PATHS.OAUTH2_CALLBACK}
          element={<MsalRedirectHandler />}
        />
        <Route
          path={PATHS.CHANGE_PASSWORD}
          element={<ChangePasswordPage />}
        />

        {/* Protected: Landing page */}
        <Route
          element={
            <ProtectedRoute>
              <Outlet />
            </ProtectedRoute>
          }
        >
          <Route
            path={PATHS.ROOT}
            element={<LandingPage />}
          />
          <Route
            path="/:module"
            element={<LandingPage />}
          />
        </Route>
        {/* Protected: Portal with PortalLayout */}
        <Route
          element={
            <ProtectedRoute>
              <PortalLayout />
            </ProtectedRoute>
          }
        >
          <Route
            path={PATHS.REPORT_VIEW}
            element={<EmbeddedReportViewer />}
          />
        </Route>

        {/* Protected: Admin with AdminLayout */}
        <Route
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route
            path={`${PATHS.ADMIN.ROOT}/*`}
            element={<AdminRoutes />}
          />
        </Route>

        {/* Fallback */}
        <Route
          path="*"
          element={
            <Navigate
              to={PATHS.ROOT}
              replace
            />
          }
        />
      </Routes>
    </Suspense>
  );
}
